import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, RiskBar, Skel, Stat } from '../components/ui'
import { money, thaiDateTime } from '../lib/format'

/**
 * Patch register and incident patch-response runbook (part 58).
 *
 * The screen is deliberately strict about two things the backend also enforces:
 *
 *   - Deployment is offered only from "approved", and the server refuses it outside the
 *     Bangkok maintenance window with 422 WINDOW_CLOSED. The button here cannot bypass that;
 *     it only saves the operator a doomed request.
 *   - The runbook is bound to a real incident id. Steps are seeded from the canonical six-step
 *     template once and then ticked individually, so progress is evidence rather than a slider.
 */

type PatchStatus = 'planned' | 'approved' | 'deployed' | 'verified'
type Exposure = 'internet' | 'partner' | 'internal' | 'isolated'

interface PatchItem {
  id: string
  title: string
  asset: string
  vendor: string
  cve_id: string
  current_version: string
  target_version: string
  cvss: number
  exposure: Exposure
  status: PatchStatus
  risk_score: number
  risk_band: 'low' | 'medium' | 'high' | 'critical'
  published_at: string
  window_date: string
  window_start: string
  window_end: string
  window_label: string
  window_open: boolean
  downtime_cost_satang: number
  owner_id?: string
  notes: string
  approved_at?: string
  deployed_at?: string
  verified_at?: string
  created_at: string
  updated_at: string
}

interface IncidentLite {
  id: string
  severity: 'sev1' | 'sev2' | 'sev3'
  title: string
  status: string
  detected_at: string
}

interface RunbookStep {
  id: number
  step_no: number
  phase: string
  title: string
  detail: string
  done: boolean
  done_at?: string
  done_by?: string
}

interface Runbook {
  incident_id: string
  steps: RunbookStep[]
  progress: { done: number; total: number; percent: number; complete: boolean }
}

const statusLabel: Record<PatchStatus, string> = {
  planned: 'วางแผน',
  approved: 'อนุมัติแล้ว',
  deployed: 'ติดตั้งแล้ว',
  verified: 'ยืนยันผลแล้ว',
}

const statusTone: Record<PatchStatus, string> = {
  planned: 'chip',
  approved: 'chip gold',
  deployed: 'chip',
  verified: 'chip',
}

const exposureLabel: Record<Exposure, string> = {
  internet: 'เปิดสู่อินเทอร์เน็ต',
  partner: 'เชื่อมกับพันธมิตร',
  internal: 'เครือข่ายภายใน',
  isolated: 'แยกเดี่ยว',
}

const nextStatus: Record<PatchStatus, PatchStatus | null> = {
  planned: 'approved',
  approved: 'deployed',
  deployed: 'verified',
  verified: null,
}

const nextAction: Record<PatchStatus, string> = {
  planned: 'อนุมัติ',
  approved: 'ติดตั้ง',
  deployed: 'ยืนยันผล',
  verified: '',
}

const severityLabel: Record<string, string> = { sev1: 'SEV1', sev2: 'SEV2', sev3: 'SEV3' }

function todayInBangkok(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Bangkok' }).format(new Date())
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

export function PatchManagement() {
  const { me } = useAuth()
  const qc = useQueryClient()
  const canWrite = me?.role === 'owner' || me?.role === 'admin' || me?.role === 'audit_manager'
  const canRunbook = canWrite || me?.role === 'auditor'

  const [statusFilter, setStatusFilter] = useState<'all' | PatchStatus>('all')
  const [notice, setNotice] = useState('')
  const [formError, setFormError] = useState('')

  const [title, setTitle] = useState('')
  const [asset, setAsset] = useState('')
  const [vendor, setVendor] = useState('')
  const [cve, setCve] = useState('')
  const [currentVersion, setCurrentVersion] = useState('')
  const [targetVersion, setTargetVersion] = useState('')
  const [cvss, setCvss] = useState('7.5')
  const [exposure, setExposure] = useState<Exposure>('internal')
  const [windowDate, setWindowDate] = useState(todayInBangkok())
  const [windowStart, setWindowStart] = useState('22:00')
  const [windowEnd, setWindowEnd] = useState('23:59')
  const [downtimeBaht, setDowntimeBaht] = useState('')
  const [notes, setNotes] = useState('')

  const [incidentId, setIncidentId] = useState('')

  const patches = useQuery({
    queryKey: ['patches', statusFilter],
    queryFn: () => api<PatchItem[]>(`/v1/patches${statusFilter === 'all' ? '' : `?status=${statusFilter}`}`),
    staleTime: 15_000,
    refetchInterval: 60_000,
  })

  const incidents = useQuery({
    queryKey: ['incidents', 'patch-runbook'],
    queryFn: () => api<IncidentLite[]>('/v1/incidents'),
    staleTime: 30_000,
  })

  const activeIncident = incidentId || (incidents.data?.[0]?.id ?? '')

  const runbook = useQuery({
    queryKey: ['runbook', activeIncident],
    queryFn: () => api<Runbook>(`/v1/incidents/${activeIncident}/runbook`),
    enabled: activeIncident !== '',
    staleTime: 10_000,
  })

  const create = useMutation({
    mutationFn: () => apiWrite<PatchItem>('/v1/patches', 'POST', {
      title: title.trim(),
      asset: asset.trim(),
      vendor: vendor.trim(),
      cve_id: cve.trim().toUpperCase(),
      current_version: currentVersion.trim(),
      target_version: targetVersion.trim(),
      cvss: Number(cvss),
      exposure,
      window_date: windowDate,
      window_start: windowStart,
      window_end: windowEnd,
      downtime_cost_satang: Math.round((Number(downtimeBaht) || 0) * 100),
      notes: notes.trim(),
    }),
    onSuccess: (row) => {
      setNotice(`ขึ้นทะเบียน ${row.title} แล้ว · ความเสี่ยง ${row.risk_score}/100 · หน้าต่าง ${row.window_label}`)
      setFormError('')
      setTitle('')
      setAsset('')
      setCve('')
      setCurrentVersion('')
      setTargetVersion('')
      setNotes('')
      setDowntimeBaht('')
      void qc.invalidateQueries({ queryKey: ['patches'] })
    },
    onError: (err) => {
      setNotice('')
      setFormError(errorText(err, 'ขึ้นทะเบียนแพตช์ไม่สำเร็จ'))
    },
  })

  const advance = useMutation({
    mutationFn: (input: { item: PatchItem; to: PatchStatus }) =>
      apiWrite<PatchItem>(`/v1/patches/${input.item.id}/advance`, 'POST', { to: input.to }),
    onSuccess: (row) => {
      setNotice(`อัปเดต ${row.title} เป็น “${statusLabel[row.status]}” แล้ว`)
      setFormError('')
      void qc.invalidateQueries({ queryKey: ['patches'] })
    },
    onError: (err) => {
      setNotice('')
      setFormError(errorText(err, 'เปลี่ยนสถานะแพตช์ไม่สำเร็จ'))
      void qc.invalidateQueries({ queryKey: ['patches'] })
    },
  })

  const seedRunbook = useMutation({
    mutationFn: () => apiWrite<Runbook>(`/v1/incidents/${activeIncident}/runbook`, 'POST', {}),
    onSuccess: (data) => {
      setNotice(`สร้างรันบุ๊ก ${data.progress.total} ขั้นตอนจากแม่แบบแล้ว`)
      setFormError('')
      void qc.invalidateQueries({ queryKey: ['runbook', activeIncident] })
    },
    onError: (err) => setFormError(errorText(err, 'สร้างรันบุ๊กไม่สำเร็จ')),
  })

  const toggleStep = useMutation({
    mutationFn: (step: RunbookStep) =>
      apiWrite<Runbook>(`/v1/incidents/${activeIncident}/runbook/${step.step_no}`, 'PATCH', { done: !step.done }),
    onSuccess: (data) => {
      setNotice(`ความคืบหน้ารันบุ๊ก ${data.progress.done}/${data.progress.total} ขั้นตอน (${data.progress.percent}%)`)
      setFormError('')
      void qc.invalidateQueries({ queryKey: ['runbook', activeIncident] })
    },
    onError: (err) => setFormError(errorText(err, 'บันทึกขั้นตอนไม่สำเร็จ')),
  })

  const rows = patches.data ?? []
  const counts = useMemo(() => {
    const out = { critical: 0, waiting: 0, open: 0, cost: 0 }
    for (const row of rows) {
      if (row.risk_band === 'critical') out.critical += 1
      if (row.status === 'planned' || row.status === 'approved') out.waiting += 1
      if (row.window_open) out.open += 1
      out.cost += row.downtime_cost_satang
    }
    return out
  }, [rows])

  const errors = patches.error ?? incidents.error
  const loading = patches.isLoading || incidents.isLoading
  const selectedIncident = (incidents.data ?? []).find((i) => i.id === activeIncident)

  return (
    <>
      <PageHead
        title="ทะเบียนแพตช์และรันบุ๊กตอบสนองเหตุ"
        sub="จัดลำดับความเสี่ยงจาก CVSS การเข้าถึง และอายุช่องโหว่ · ติดตั้งได้เฉพาะในหน้าต่างซ่อมบำรุงเวลาไทย · ทุกการเปลี่ยนสถานะถูกบันทึก"
        actions={
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => {
              void patches.refetch()
              if (activeIncident) void runbook.refetch()
            }}
            disabled={patches.isFetching}
          >
            {patches.isFetching ? 'กำลังโหลด…' : 'รีเฟรช'}
          </button>
        }
      />

      <div
        className="card card-pad"
        style={{
          marginBottom: 18,
          borderColor: 'rgba(200, 169, 106, 0.35)',
          background: 'linear-gradient(180deg, rgba(200, 169, 106, 0.07), transparent)',
        }}
      >
        <div className="row" style={{ alignItems: 'flex-start', gap: 12 }}>
          <span className="chip gold">นโยบาย</span>
          <div>
            <div style={{ fontSize: 13.5, lineHeight: 1.75 }}>
              การติดตั้งแพตช์ต้องอยู่ในหน้าต่างซ่อมบำรุงที่กำหนดเป็นเวลา Asia/Bangkok และต้องผ่านลำดับ
              วางแผน → อนุมัติ → ติดตั้ง → ยืนยันผล เท่านั้น
            </div>
            <div className="dim" style={{ fontSize: 12, lineHeight: 1.75, marginTop: 6 }}>
              ระบบจะปฏิเสธการติดตั้งนอกหน้าต่างด้วยรหัส 422 และปฏิเสธการข้ามขั้นด้วยรหัส 409
              แม้ผู้ใช้จะเรียก API โดยตรงก็ตาม
            </div>
          </div>
        </div>
      </div>

      <div className="grid-4" style={{ marginBottom: 20 }}>
        <Stat label="แพตช์ในทะเบียน" value={rows.length} unit="รายการ" foot="ตามตัวกรองที่เลือก" />
        <Stat label="ความเสี่ยงวิกฤต" value={counts.critical} unit="รายการ" foot="คะแนนตั้งแต่ 80 ขึ้นไป" />
        <Stat label="รออนุมัติหรือติดตั้ง" value={counts.waiting} unit="รายการ" foot="ยังไม่อยู่ในสถานะติดตั้งแล้ว" />
        <Stat label="หน้าต่างเปิดอยู่" value={counts.open} unit="รายการ" foot="ติดตั้งได้ในขณะนี้ตามเวลาไทย" />
      </div>

      <div className="card card-pad" style={{ marginBottom: 20 }}>
        <div className="card-label">ขึ้นทะเบียนแพตช์ใหม่</div>
        <p className="dim" style={{ fontSize: 12, lineHeight: 1.8, marginTop: 10 }}>
          คะแนนความเสี่ยงคำนวณใหม่ทุกครั้งที่อ่าน: CVSS ให้น้ำหนัก 70 คะแนน การเข้าถึงสูงสุด 18 คะแนน
          และอายุช่องโหว่สูงสุด 12 คะแนน (อิ่มตัวที่ 180 วัน)
        </p>

        <div className="grid-2">
          <div className="field" style={{ marginTop: 16 }}>
            <label htmlFor="patch-title">ชื่องานแพตช์</label>
            <input id="patch-title" className="input" style={{ width: '100%' }} maxLength={200}
              value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="เช่น ปิดช่องโหว่ OpenSSL บนเครื่องบริการชำระเงิน" />
          </div>
          <div className="field" style={{ marginTop: 16 }}>
            <label htmlFor="patch-asset">เครื่องหรือระบบ</label>
            <input id="patch-asset" className="input" style={{ width: '100%' }} maxLength={200}
              value={asset} onChange={(e) => setAsset(e.target.value)}
              placeholder="payments-api-01" />
          </div>
        </div>

        <div className="grid-3">
          <div className="field">
            <label htmlFor="patch-vendor">ผู้ผลิต</label>
            <input id="patch-vendor" className="input" style={{ width: '100%' }} maxLength={120}
              value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="OpenSSL" />
          </div>
          <div className="field">
            <label htmlFor="patch-cve">รหัส CVE</label>
            <input id="patch-cve" className="input mono" style={{ width: '100%' }} maxLength={40}
              value={cve} onChange={(e) => setCve(e.target.value)} placeholder="CVE-2026-12345" />
          </div>
          <div className="field">
            <label htmlFor="patch-cvss">คะแนน CVSS</label>
            <input id="patch-cvss" className="input tnum" style={{ width: '100%' }} type="number"
              min="0" max="10" step="0.1" value={cvss} onChange={(e) => setCvss(e.target.value)} />
          </div>
        </div>

        <div className="grid-3">
          <div className="field">
            <label htmlFor="patch-current">เวอร์ชันปัจจุบัน</label>
            <input id="patch-current" className="input mono" style={{ width: '100%' }} maxLength={80}
              value={currentVersion} onChange={(e) => setCurrentVersion(e.target.value)} placeholder="3.0.11" />
          </div>
          <div className="field">
            <label htmlFor="patch-target">เวอร์ชันเป้าหมาย</label>
            <input id="patch-target" className="input mono" style={{ width: '100%' }} maxLength={80}
              value={targetVersion} onChange={(e) => setTargetVersion(e.target.value)} placeholder="3.0.13" />
          </div>
          <div className="field">
            <label htmlFor="patch-exposure">ระดับการเข้าถึง</label>
            <select id="patch-exposure" className="select" style={{ width: '100%' }}
              value={exposure} onChange={(e) => setExposure(e.target.value as Exposure)}>
              <option value="internet">เปิดสู่อินเทอร์เน็ต</option>
              <option value="partner">เชื่อมกับพันธมิตร</option>
              <option value="internal">เครือข่ายภายใน</option>
              <option value="isolated">แยกเดี่ยว</option>
            </select>
          </div>
        </div>

        <div className="grid-3">
          <div className="field">
            <label htmlFor="patch-window-date">วันที่หน้าต่างซ่อมบำรุง (เวลาไทย)</label>
            <input id="patch-window-date" className="input" style={{ width: '100%' }} type="date"
              value={windowDate} onChange={(e) => setWindowDate(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="patch-window-start">เริ่ม (HH:MM)</label>
            <input id="patch-window-start" className="input tnum" style={{ width: '100%' }} type="time"
              value={windowStart} onChange={(e) => setWindowStart(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="patch-window-end">สิ้นสุด (HH:MM)</label>
            <input id="patch-window-end" className="input tnum" style={{ width: '100%' }} type="time"
              value={windowEnd} onChange={(e) => setWindowEnd(e.target.value)} />
          </div>
        </div>

        <div className="grid-2">
          <div className="field">
            <label htmlFor="patch-cost">ต้นทุนการหยุดให้บริการโดยประมาณ (บาท)</label>
            <input id="patch-cost" className="input tnum" style={{ width: '100%' }} type="number"
              min="0" step="0.01" value={downtimeBaht} onChange={(e) => setDowntimeBaht(e.target.value)}
              placeholder="0.00" />
          </div>
          <div className="field">
            <label htmlFor="patch-notes">บันทึก</label>
            <input id="patch-notes" className="input" style={{ width: '100%' }} maxLength={4000}
              value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="แผนย้อนกลับ ผู้รับผิดชอบ หรือผลทดสอบบน staging" />
          </div>
        </div>

        <div className="row" style={{ marginTop: 16, gap: 10 }}>
          <button
            className="btn btn-primary"
            disabled={!canWrite || create.isPending || title.trim() === '' || asset.trim() === ''
              || currentVersion.trim() === '' || targetVersion.trim() === ''}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'กำลังบันทึก…' : 'ขึ้นทะเบียน'}
          </button>
          {!canWrite ? (
            <span className="dim" style={{ fontSize: 11.5 }}>บทบาทของคุณอ่านทะเบียนได้ แต่ไม่มีสิทธิ์แก้ไข</span>
          ) : null}
        </div>

        {formError ? <div className="form-error">{formError}</div> : null}
        {notice ? (
          <div className="dim" style={{ fontSize: 12, marginTop: 12, color: 'var(--color-gold)', lineHeight: 1.7 }}>
            {notice}
          </div>
        ) : null}
      </div>

      <div className="filters">
        <label htmlFor="patch-status" className="dim" style={{ fontSize: 12 }}>สถานะ</label>
        <select id="patch-status" className="select" value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as 'all' | PatchStatus)}>
          <option value="all">ทั้งหมด</option>
          <option value="planned">วางแผน</option>
          <option value="approved">อนุมัติแล้ว</option>
          <option value="deployed">ติดตั้งแล้ว</option>
          <option value="verified">ยืนยันผลแล้ว</option>
        </select>
        <span className="chip" style={{ marginLeft: 'auto' }}>{rows.length} รายการ</span>
        <span className="dim" style={{ fontSize: 11.5 }}>ต้นทุนหยุดรวม {money(counts.cost)}</span>
      </div>

      {loading ? <Skel h={260} /> : errors ? (
        <div className="form-error">{errorText(errors, 'โหลดทะเบียนแพตช์ไม่สำเร็จ')}</div>
      ) : rows.length === 0 ? (
        <Empty
          title="ยังไม่มีแพตช์ในทะเบียน"
          body="ขึ้นทะเบียนแพตช์แรกด้านบน ระบบจะคำนวณความเสี่ยงและรออนุมัติตามลำดับ"
        />
      ) : (
        <div className="card" style={{ padding: 0, marginBottom: 20 }}>
          <div className="table-wrap" style={{ border: 'none' }}>
            <table className="table data">
              <thead>
                <tr>
                  <th>ความเสี่ยง</th>
                  <th>แพตช์ / เครื่อง</th>
                  <th>CVE · CVSS</th>
                  <th>การเข้าถึง</th>
                  <th>สถานะ</th>
                  <th>หน้าต่างซ่อมบำรุง</th>
                  <th className="num">ต้นทุนหยุด</th>
                  <th>การดำเนินการ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const next = nextStatus[row.status]
                  return (
                    <tr key={row.id}>
                      <td><RiskBar score={row.risk_score} /></td>
                      <td>
                        <div>{row.title}</div>
                        <div className="dim mono" style={{ fontSize: 11, marginTop: 3 }}>
                          {row.asset} · {row.current_version} → {row.target_version}
                        </div>
                      </td>
                      <td>
                        <div className="mono" style={{ fontSize: 11.5 }}>{row.cve_id || '—'}</div>
                        <div className="dim tnum" style={{ fontSize: 11, marginTop: 3 }}>{row.cvss.toFixed(1)}</div>
                      </td>
                      <td><span className="chip">{exposureLabel[row.exposure] ?? row.exposure}</span></td>
                      <td>
                        <span className={statusTone[row.status] ?? 'chip'}>{statusLabel[row.status] ?? row.status}</span>
                        {row.verified_at ? (
                          <div className="dim" style={{ fontSize: 10.5, marginTop: 4 }}>
                            ยืนยัน {thaiDateTime(row.verified_at)}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <div style={{ fontSize: 11.5 }}>{row.window_label}</div>
                        <div className="dim" style={{ fontSize: 10.5, marginTop: 4 }}>
                          {row.window_open ? 'เปิดอยู่ตอนนี้' : 'ปิดอยู่'}
                        </div>
                      </td>
                      <td className="num">{money(row.downtime_cost_satang)}</td>
                      <td>
                        {next ? (
                          <button
                            className="btn btn-ghost btn-sm"
                            disabled={!canWrite || advance.isPending}
                            onClick={() => advance.mutate({ item: row, to: next })}
                          >
                            {advance.isPending && advance.variables?.item.id === row.id
                              ? 'กำลังบันทึก…'
                              : nextAction[row.status]}
                          </button>
                        ) : (
                          <span className="dim" style={{ fontSize: 11 }}>เสร็จสิ้น</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card card-pad">
        <div className="card-label">รันบุ๊กตอบสนองเหตุสำหรับแพตช์</div>
        <p className="dim" style={{ fontSize: 12, lineHeight: 1.8, marginTop: 10 }}>
          เลือกเหตุการณ์ที่ต้องแก้ด้วยแพตช์ ระบบจะแสดงหกขั้นตอนมาตรฐานและความคืบหน้า
          ขั้นตอนถูกคัดลอกไว้กับเหตุการณ์นั้น จึงแก้แม่แบบย้อนหลังไม่ได้
        </p>

        <div className="row" style={{ gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
          <label htmlFor="runbook-incident" className="dim" style={{ fontSize: 12 }}>เหตุการณ์</label>
          <select
            id="runbook-incident"
            className="select"
            style={{ minWidth: 420, maxWidth: '100%' }}
            value={activeIncident}
            onChange={(e) => setIncidentId(e.target.value)}
            disabled={incidents.isLoading || (incidents.data ?? []).length === 0}
          >
            {(incidents.data ?? []).length === 0 ? <option value="">— ยังไม่มีเหตุการณ์ —</option> : null}
            {(incidents.data ?? []).map((incident) => (
              <option key={incident.id} value={incident.id}>
                {severityLabel[incident.severity] ?? incident.severity} · {incident.title}
              </option>
            ))}
          </select>
          {selectedIncident ? (
            <span className="dim" style={{ fontSize: 11.5 }}>
              ตรวจพบ {thaiDateTime(selectedIncident.detected_at)} · สถานะ {selectedIncident.status}
            </span>
          ) : null}
        </div>

        {!activeIncident ? (
          <Empty title="ยังไม่มีเหตุการณ์ให้ผูก" body="เปิดเหตุการณ์ก่อน แล้วกลับมาเลือกรันบุ๊กของเหตุนั้น" />
        ) : runbook.isLoading ? (
          <Skel h={180} />
        ) : runbook.isError ? (
          <div className="form-error">{errorText(runbook.error, 'โหลดรันบุ๊กไม่สำเร็จ')}</div>
        ) : (
          <>
            <div className="row" style={{ marginTop: 16, gap: 12 }}>
              <div className="bar" style={{ flex: 1, height: 6 }}>
                <i style={{ width: `${runbook.data?.progress.percent ?? 0}%` }} />
              </div>
              <span className="tnum" style={{ fontSize: 12 }}>
                {runbook.data?.progress.done ?? 0}/{runbook.data?.progress.total ?? 0} ขั้นตอน
                {' · '}{runbook.data?.progress.percent ?? 0}%
              </span>
            </div>

            {runbook.data && runbook.data.steps.length === 0 ? (
              <div style={{ marginTop: 16 }}>
                <div className="dim" style={{ fontSize: 12, marginBottom: 10 }}>
                  ยังไม่มีขั้นตอนสำหรับเหตุนี้ ระบบจะคัดลอกแม่แบบหกขั้นตอนมาให้ครั้งเดียว
                </div>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={!canRunbook || seedRunbook.isPending}
                  onClick={() => seedRunbook.mutate()}
                >
                  {seedRunbook.isPending ? 'กำลังสร้าง…' : 'สร้างรันบุ๊กจากแม่แบบ'}
                </button>
              </div>
            ) : (
              <div className="stack" style={{ marginTop: 16 }}>
                {(runbook.data?.steps ?? []).map((step) => (
                  <div key={step.id} className="card card-pad" style={{ padding: 14 }}>
                    <div className="row" style={{ alignItems: 'flex-start', gap: 10 }}>
                      <input
                        type="checkbox"
                        checked={step.done}
                        disabled={!canRunbook || toggleStep.isPending}
                        onChange={() => toggleStep.mutate(step)}
                        aria-label={`ขั้นตอนที่ ${step.step_no}`}
                        style={{ marginTop: 3 }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12.5, lineHeight: 1.6 }}>
                          {step.step_no}. {step.title}
                          <span className="chip" style={{ marginLeft: 8 }}>{step.phase}</span>
                        </div>
                        <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.75, marginTop: 6 }}>
                          {step.detail}
                        </div>
                        {step.done && step.done_at ? (
                          <div className="dim" style={{ fontSize: 10.5, marginTop: 6 }}>
                            ทำเสร็จ {thaiDateTime(step.done_at)}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}
